import { Component, Input, OnChanges, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CloseModalPopupAction } from '@app/finxone-web-frontend/app/lib/actions/page-context.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { PageHooksService } from '@app/finxone-web-frontend/app/lib/services/hooks-service/page-hooks-service.service';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  isDesktopNavbarVisible,
  toggleElementVisibilityById,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ActiveDevice,
  APP_ZONES,
  baseCssConfigStyle,
  ContainerType,
  cssToNgStyle,
  LayoutNames,
  NavBarDesktop,
  PageHooks,
  TransitionPresetType,
  UiPageConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { debounceTime, delay, distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import { pageTransition, pageTransitionPresets } from '../parent-container/page-transitions';
import { WidgetLayoutService } from '../widget-layout/widget-layout.service';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-desktop-container',
  templateUrl: './desktop-container.component.html',
  styleUrls: ['./desktop-container.component.scss'],
  animations: [pageTransition],
})
export class DesktopContainerComponent implements OnInit, OnChanges, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() zoneUrlInput = '';
  @Input() pageUrlInput: string;
  @Input() menuType: string;
  @Input() parentComponent: string;
  @Input() navDirection: keyof TransitionPresetType = 'forward';
  public pageTransitionDone = false;

  public navBarDesktopConfig: NavBarDesktop;
  private desktopConfigService: Subscription;
  isWorkflow: boolean;
  currentPageDetail: UiPageConfig;
  public LayoutNames = LayoutNames;

  public gridsterMargins = {
    top: 0,
    left: 0,
    right: 0,
    unit: 'em',
  };

  // Signal to track whether the navigation menu is hidden or visible
  hideNavigationMenu = signal(false);
  public footerLayoutStyles = {};
  public footerLayoutHeight: number;
  public mainWidgetStyling = {};
  public pageContentStyle: baseCssConfigStyle;

  constructor(
    private readonly configService: ConfigService,
    public readonly hookService: PageHooksService,
    private readonly actions: Actions,
    private readonly renderer: Renderer2,
    private readonly widgetLayoutService: WidgetLayoutService,
    private readonly store: Store,
    public outlet: RouterOutlet,
  ) {
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
  }
  ngOnInit() {
    this.onGetCloseModalPopupEvent();
    this.desktopConfig();
    this.subscribeToFooterHeight();
  }

  private subscribeToFooterHeight(): void {
    this.widgetLayoutService.footerHeight$
      .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((height: number) => {
        this.footerLayoutHeight = height;
        if (this.currentPageDetail?.footerLayoutEnabled) {
          this.updateMainWidgetHeight();
          this.setFooterLayoutStyles();
        }
      });
  }

  /**
   * Updates the main widget height to prevent overlap with the sticky footer.
   *
   * When sticky footer and body scrolling are enabled, the height is set to:
   *
   *     100vh - (footerLayoutHeight)
   *
   * This ensures the main content can scroll without overlapping the footer.
   */
  private updateMainWidgetHeight() {
    this.mainWidgetStyling = {};
    if (!this.currentPageDetail?.footerLayoutEnabled) return;

    if (
      this.currentPageDetail?.footerConfiguration?.enableStickyFooter &&
      this.currentPageDetail?.pageConfiguration?.isBodyScrollingEnabled
    ) {
      this.mainWidgetStyling = {
        height: `calc(100vh - ${this.footerLayoutHeight}px)`,
        overflow: 'scroll',
        display: 'block',
        paddingBottom: '30px',
      };
    }
  }

  private onGetCloseModalPopupEvent() {
    this.actions
      .pipe(ofActionDispatched(CloseModalPopupAction), delay(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCurrentPageConfigByPageUrl();
      });
  }

  private desktopConfig() {
    const isLogoutOutZones = [
      formatNameAsUrl(APP_ZONES.SIGNED_OUT_FIRST_INTRODUCTION),
      formatNameAsUrl(APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD),
      formatNameAsUrl(APP_ZONES.SIGNED_OUT_INTRODUCTION),
      formatNameAsUrl(APP_ZONES.ONBOARDING),
    ];
    if (isLogoutOutZones.includes(this.zoneUrlInput)) {
      this.getCurrentPageConfigByPageUrl();
    } else {
      this.desktopConfigService = this.configService
        .getNavBarDesktopConfig()
        .pipe(takeUntil(this.destroy$))
        .subscribe((config) => {
          this.getCurrentPageConfigByPageUrl();
          this.navBarDesktopConfig = config;
          if (!this.isPageAsModal) {
            this.menuType = isDesktopNavbarVisible(this.zoneUrlInput)
              ? 'none'
              : this.navBarDesktopConfig.menuType;
          }
        });
    }
  }

  get isPageAsModal() {
    return this.parentComponent === 'PageAsModalComponent' || (this.menuType === 'modal' && !this.isWorkflow);
  }

  ngOnChanges(): void {
    this.desktopConfigService?.unsubscribe();
    this.desktopConfig();
  }

  ngOnDestroy() {
    this.desktopConfigService?.unsubscribe();
  }

  private getCurrentPageConfigByPageUrl(): void {
    this.configService
      .getPageUrlPage(this.pageUrlInput)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.pageConfiguration) {
          this.currentPageDetail = result;
          this.setFooterLayoutStyles();
          this.updateMainWidgetHeight();
          this.applyPageStyles();
          this.determineNavigationMenuVisibility();

          this.gridsterMargins = result.pageConfiguration.gridsterMargin;
          this.checkAndExecutePageHooks(result.pageConfiguration?.pageHooks);
        }
      });
  }

  private checkAndExecutePageHooks(pageHooks: PageHooks[] | undefined): void {
    if (pageHooks) {
      pageHooks.forEach((hook) => {
        if (hook.enabled) {
          const hookFunction = this.hookService.getHook(
            'hook' + hook.name.charAt(0).toUpperCase() + hook.name.slice(1),
          );
          if (typeof hookFunction === 'function' && !this.isWorkflow) {
            hookFunction();
          }
        }
      });
    }
  }

  private applyPageStyles(): void {
    if (this.currentPageDetail?.containerType === ContainerType.PAGE) {
      this.pageContentStyle = {
        ...this.configService.getPageContentStyle(this.currentPageDetail?.pageConfiguration),
        ...cssToNgStyle(this.currentPageDetail?.pageConfiguration?.outerCss ?? ''),
      };
    }
  }

  private determineNavigationMenuVisibility(): void {
    const hideNavigationMenu = this.currentPageDetail?.pageConfiguration?.hideNavigationMenu;
    this.hideNavigationMenu.set(!!hideNavigationMenu);

    /**
     * Toggles the visibility of the application footer based on the navigation menu's state.
     *
     * - If the navigation menu is hidden (determined by `hideNavigationMenu()`), the footer is modified to be hidden.
     * - Otherwise, the footer is displayed.
     *
     * @remarks
     * - The `toggleElementVisibilityById` function is used to set the visibility of the footer by its DOM ID `#desktop-nav-div`.
     * - This is added because we do have modal and page types, so it should hide both router's footer * element when hideNavigationMenu is enabled.
     */
    if (this.hideNavigationMenu()) {
      // Hide the footer when the navigation menu is hidden
      toggleElementVisibilityById('#desktop-nav-div', true, this.renderer);
    } else {
      toggleElementVisibilityById('#desktop-nav-div', false, this.renderer);
    }
  }

  private setFooterLayoutStyles(): void {
    this.footerLayoutStyles = {};
    if (!this.currentPageDetail?.footerLayoutEnabled) return;

    const { footerConfiguration, pageConfiguration, containerType } = this.currentPageDetail;
    const isSticky = !!footerConfiguration?.enableStickyFooter;

    const background = footerConfiguration?.enableScrollFade
      ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, var(--color-background) 20px)'
      : 'var(--color-background)';
    const paddingTop = this.currentPageDetail?.footerConfiguration?.enableScrollFade ? '20px' : '';

    const positionStyles: baseCssConfigStyle = {
      position: isSticky ? 'fixed' : 'static',
      bottom: isSticky ? '0' : undefined,
      ...(isSticky && !this.hideNavigationMenu() && this.menuType === 'vertical'
        ? { left: `${this.navBarDesktopConfig?.width}px`, right: '0' }
        : {}),
    };

    if (containerType === ContainerType.MODAL) {
      const modalCssDisabled = !pageConfiguration?.modalConfiguration?.cssEnabled;
      const modalHeight = `${pageConfiguration?.modalConfiguration?.modalSizeHeight}${pageConfiguration?.modalConfiguration?.modalSizeMarginUnit}`;
      const viewPort = this.isWorkflow ? '30vh' : '15vh';

      if (modalCssDisabled) {
        positionStyles[
          'width'
        ] = `${pageConfiguration?.modalConfiguration?.modalSizeWidth}${pageConfiguration?.modalConfiguration?.modalSizeMarginUnit}`;
        positionStyles['bottom'] = `calc((100vh - ${viewPort}) - ${modalHeight})`;

        if (!isSticky) positionStyles['position'] = 'static';
      } else {
        positionStyles['left'] = '0';
        positionStyles['right'] = '0';
      }
    }

    this.footerLayoutStyles = {
      background,
      paddingTop,
      ...positionStyles,
      ...cssToNgStyle(this.currentPageDetail?.footerConfiguration?.outerCss ?? ''),
    };
  }

  public getParams(outlet: RouterOutlet) {
    // Return the current page and params as the animation state
    const page = outlet?.activatedRouteData?.['page'] || outlet?.activatedRoute?.snapshot?.params?.['page'];
    const params = page ? this.getAnimationParamsForPage() : pageTransitionPresets['']; // fallback to default animation preset ['']

    return {
      value: page,
      params,
    };
  }

  private getAnimationParamsForPage() {
    const transitionPreset = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.uiSettings?.[
      ActiveDevice.DESKTOP
    ]?.pageTransition;
    let preset = '';

    if (typeof transitionPreset === 'string') {
      preset = transitionPreset ?? '';
    }
    if (typeof transitionPreset === 'object') {
      preset = transitionPreset[this.navDirection] ?? '';
    }
    return pageTransitionPresets[preset];
  }

  public onTransitionDone() {
    // primary use case to only show footer after page transition finishes
    this.pageTransitionDone = true;
  }

  public onTransitionStart() {
    // Hide footer when page transition start
    this.pageTransitionDone = false;
  }
}
